import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from "react";
import { CheckCheck } from "lucide-react";
import "./styles/UserProfileIcon.css";
import { getUserAvatarUrl } from "../../services/helpers/ImageUrlHelper";

type IconSize = "sm" | "md" | "lg" | "xl";

interface UserProfileIconProps {
  userName: string;
  userId?: string;
  isVerified: boolean;
  size?: IconSize;
  darkBackground?: boolean;
  children?: React.ReactNode;
}

const UserProfileIcon: React.FC<UserProfileIconProps> = ({
  userName,
  userId,
  isVerified: _isVerified = false,
  size = "md",
  darkBackground = false,
  children,
}) => {
  const isVerified = false;
  const [imgError, setImgError] = useState(false);
  const [isLongName, setIsLongName] = useState(false);
  const nameRef = useRef<HTMLSpanElement | null>(null);

  // Reset error state when userId changes (e.g. reused component)
  useEffect(() => {
    setImgError(false);
  }, [userId]);

  // Detect overflow once per name/size in default (single-line) state, then latch.
  // No ResizeObserver: applying the `.long` class changes the element's height, which can
  // re-trigger observers and oscillate inside scrolling/animated parents.
  useLayoutEffect(() => {
    setIsLongName(false);
    const raf = requestAnimationFrame(() => {
      const el = nameRef.current;
      if (!el) return;
      setIsLongName(el.scrollWidth > el.clientWidth + 1);
    });
    return () => cancelAnimationFrame(raf);
  }, [userName, size]);

  // Window resize handler — rare and bounded, safe to remeasure here
  useEffect(() => {
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setIsLongName(false);
        raf = requestAnimationFrame(() => {
          const el = nameRef.current;
          if (!el) return;
          setIsLongName(el.scrollWidth > el.clientWidth + 1);
        });
      });
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, []);

  const profilePictureUrl = userId ? getUserAvatarUrl(userId) : null;

  // If we have a profile URL and it hasn't failed yet, try it. Otherwise fallback to generic dummy image
  const imageSource = (profilePictureUrl && !imgError) ? profilePictureUrl : "/default-profile-fallback.png";

  return (
    <div className="user-profile-icon-container">
      <div className="user-profile-icon-wrapper">
        <div
          className={`user-profile-icon-inner user-profile-icon-${size} ${isVerified ? "user-profile-icon-verified" : ""
            }`}
        >
          {/* Profile Image or Fallback */}
          <img
            src={imageSource}
            alt={`${userName} profile icon`}
            className="user-profile-image"
            loading="lazy"
            onError={() => setImgError(true)}
          />
        </div>

        {isVerified && (
          <div
            className={`user-profile-badge user-profile-badge-${size}`}
            aria-label="Verified User"
          >
            <CheckCheck className="user-profile-badge-icon" strokeWidth={3} />
            <span className="user-profile-badge-tooltip">Verified User</span>
          </div>
        )}
      </div>

      <div className="user-profile-info">
        <span
          ref={nameRef}
          className={`user-profile-name-${size} ${darkBackground ? "user-profile-name-dark" : "user-profile-name-light"} ${isLongName ? "user-profile-name-long" : ""}`}
          title={userName}
        >
          {userName}
        </span>
        {isVerified && (
          <span className={`user-profile-verified-label ${darkBackground ? "user-profile-verified-label-dark" : "user-profile-verified-label-light"}`}>Verified User</span>
        )}
        {children && <div style={{ marginTop: '8px' }}>{children}</div>}
      </div>
    </div>
  );
};

export default UserProfileIcon;
